import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  createPage,
  getPages,
  getPageById,
  updatePage,
  deletePage,
} from '@app/api/pages.api';
import { Page, CreatePageDto, UpdatePageDto, PageFilters, PagePaginatedResponse } from '@app/types/api.types';

export interface PagesState {
  items: Page[];
  currentItem: Page | null;
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
  filters: PageFilters;
  loading: boolean;
  error: string | null;
}

const initialState: PagesState = {
  items: [],
  currentItem: null,
  pagination: {
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
    hasNext: false,
    hasPrev: false,
  },
  filters: {
    page: 1,
    limit: 10,
  },
  loading: false,
  error: null,
};

export const fetchPages = createAsyncThunk(
  'pages/fetchPages',
  async (filters: PageFilters = { page: 1, limit: 10 }, { rejectWithValue }) => {
    try {
      const response = await getPages(filters);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch pages');
    }
  }
);

export const fetchPageById = createAsyncThunk(
  'pages/fetchPageById',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await getPageById(id);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch page');
    }
  }
);

export const createPageAction = createAsyncThunk(
  'pages/createPage',
  async (data: CreatePageDto, { rejectWithValue }) => {
    try {
      const response = await createPage(data);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to create page');
    }
  }
);

export const updatePageAction = createAsyncThunk(
  'pages/updatePage',
  async ({ id, data }: { id: string; data: UpdatePageDto }, { rejectWithValue }) => {
    try {
      const response = await updatePage(id, data);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to update page');
    }
  }
);

export const deletePageAction = createAsyncThunk(
  'pages/deletePage',
  async (id: string, { rejectWithValue }) => {
    try {
      await deletePage(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to delete page');
    }
  }
);

const pagesSlice = createSlice({
  name: 'pages',
  initialState,
  reducers: {
    setFilters: (state, action: PayloadAction<Partial<PageFilters>>) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearCurrentItem: (state) => {
      state.currentItem = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch Pages
    builder.addCase(fetchPages.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchPages.fulfilled, (state, action) => {
      state.loading = false;
      state.items = action.payload.data;
      state.pagination = {
        page: action.payload.page,
        limit: action.payload.limit,
        total: action.payload.total,
        totalPages: action.payload.totalPages || Math.ceil(action.payload.total / action.payload.limit),
        hasNext: action.payload.hasNext || action.payload.page < (action.payload.totalPages || Math.ceil(action.payload.total / action.payload.limit)),
        hasPrev: action.payload.hasPrev || action.payload.page > 1,
      };
    });
    builder.addCase(fetchPages.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Fetch Page By ID
    builder.addCase(fetchPageById.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchPageById.fulfilled, (state, action) => {
      state.loading = false;
      state.currentItem = action.payload;
    });
    builder.addCase(fetchPageById.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Create Page
    builder.addCase(createPageAction.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(createPageAction.fulfilled, (state, action) => {
      state.loading = false;
      state.items.unshift(action.payload);
    });
    builder.addCase(createPageAction.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Update Page
    builder.addCase(updatePageAction.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(updatePageAction.fulfilled, (state, action) => {
      state.loading = false;
      const index = state.items.findIndex((item) => item.id === action.payload.id);
      if (index !== -1) {
        state.items[index] = action.payload;
      }
      if (state.currentItem?.id === action.payload.id) {
        state.currentItem = action.payload;
      }
    });
    builder.addCase(updatePageAction.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Delete Page
    builder.addCase(deletePageAction.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(deletePageAction.fulfilled, (state, action) => {
      state.loading = false;
      state.items = state.items.filter((item) => item.id !== action.payload);
      if (state.currentItem?.id === action.payload) {
        state.currentItem = null;
      }
    });
    builder.addCase(deletePageAction.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });
  },
});

export const { setFilters, clearCurrentItem } = pagesSlice.actions;
export default pagesSlice.reducer;

